import { AdbReverseNotSupportedError, AdbSubprocessNoneProtocol, } from "@yume-chan/adb";
import { AbortController, DecodeUtf8Stream, InspectStream, ReadableStream, SplitStringStream, WrapWritableStream, WritableStream, } from "@yume-chan/stream-extra";
import { ScrcpyControlMessageSerializer } from "../control/index.js";
import { ScrcpyDeviceMessageDeserializeStream } from "../device-message/index.js";
import { DEFAULT_SERVER_PATH } from "../options/index.js";
class ArrayToStream extends ReadableStream {
    array;
    index = 0;
    constructor(array) {
        super({
            start: async () => {
                await Promise.resolve();
                this.array = array;
            },
            pull: (controller) => {
                if (this.index < this.array.length) {
                    controller.enqueue(this.array[this.index]);
                    this.index += 1;
                }
                else {
                    controller.close();
                }
            },
        });
    }
}
class ConcatStream extends ReadableStream {
    streams;
    index = 0;
    reader;
    constructor(...streams) {
        super({
            start: async (controller) => {
                await Promise.resolve();
                this.streams = streams;
                this.advance(controller);
            },
            pull: async (controller) => {
                const result = await this.reader.read();
                if (!result.done) {
                    controller.enqueue(result.value);
                    return;
                }
                this.advance(controller);
            },
        });
    }
    advance(controller) {
        if (this.index < this.streams.length) {
            this.reader = this.streams[this.index].getReader();
            this.index += 1;
        }
        else {
            controller.close();
        }
    }
}
export class ScrcpyExitedError extends Error {
    output;
    constructor(output) {
        super("scrcpy server exited prematurely");
        this.output = output;
    }
}
export class AdbScrcpyClient {
    static pushServer(adb, path = DEFAULT_SERVER_PATH) {
        let sync;
        return new WrapWritableStream({
            async start() {
                sync = await adb.sync();
                return sync.write(path);
            },
            async close() {
                await sync.dispose();
            },
        });
    }
    static async start(adb, path, version, options) {
        let connection;
        let process;
        try {
            try {
                connection = options.createConnection(adb);
                await connection.initialize();
            }
            catch (e) {
                if (e instanceof AdbReverseNotSupportedError) {
                    // When reverse tunnel is not supported, try forward tunnel.
                    options.value.tunnelForward = true;
                    connection = options.createConnection(adb);
                    await connection.initialize();
                }
                else {
                    connection = undefined;
                    throw e;
                }
            }
            process = await adb.subprocess.spawn([
                // cspell: disable-next-line
                `CLASSPATH=${path}`,
                "app_process",
                /* unused */ "/",
                "com.genymobile.scrcpy.Server",
                version,
                ...options.formatServerArguments(),
            ], {
                // Scrcpy server doesn't use stderr,
                // so disable Shell Protocol to simplify processing
                protocols: [AdbSubprocessNoneProtocol],
            });
            const stdout = process.stdout
                .pipeThrough(new DecodeUtf8Stream())
                .pipeThrough(new SplitStringStream("\n"));
            // Read stdout, otherwise `process.exit` won't resolve.
            const output = [];
            const abortController = new AbortController();
            const pipe = stdout
                .pipeTo(new WritableStream({
                write(chunk) {
                    output.push(chunk);
                },
            }), {
                signal: abortController.signal,
                preventCancel: true,
            })
                .catch((e) => {
                void e;
            });
            const result = await Promise.race([
                process.exit,
                connection.getStreams(),
            ]);
            if (typeof result === "number") {
                throw new ScrcpyExitedError(output);
            }
            abortController.abort();
            await pipe;
            const [videoStream, controlStream] = result;
            return new AdbScrcpyClient(options, process, new ConcatStream(new ArrayToStream(output), stdout), videoStream, controlStream);
        }
        catch (e) {
            await process?.kill();
            throw e;
        }
        finally {
            connection?.dispose();
        }
    }
    /**
     * This method will modify the given `options`,
     * so don't reuse it elsewhere.
     */
    static async getEncoders(adb, path, version, options) {
        Object.assign(options.value, {
            // Provide an invalid encoder name
            // So the server will return all available encoders
            encoderName: "_",
            // Disable control for faster connection in 1.22+
            control: false,
            sendDeviceMeta: false,
            sendDummyByte: false,
        });
        // Scrcpy server will open connections, before initializing encoder
        // Thus although an invalid encoder name is given, the start process will success
        const client = await AdbScrcpyClient.start(adb, path, version, options);
        const encoderNameRegex = options.getOutputEncoderNameRegex();
        const encoders = [];
        await client.stdout.pipeTo(new WritableStream({
            write(line) {
                const match = line.match(encoderNameRegex);
                if (match) {
                    encoders.push(match[1]);
                }
            },
        }));
        return encoders;
    }
    /**
     * This method will modify the given `options`,
     * so don't reuse it elsewhere.
     */
    static async getDisplays(adb, path, version, options) {
        Object.assign(options.value, {
            // Similar to `getEncoders`, pass an invalid option and parse the output
            displayId: -1,
            // Disable control for faster connection in 1.22+
            control: false,
            sendDeviceMeta: false,
            sendDummyByte: false,
        });
        try {
            // Server will exit before opening connections when an invalid display id was given.
            await AdbScrcpyClient.start(adb, path, version, options);
        }
        catch (e) {
            if (e instanceof ScrcpyExitedError) {
                const displayIdRegex = /\s+scrcpy --display (\d+)/;
                const displays = [];
                for (const line of e.output) {
                    const match = line.match(displayIdRegex);
                    if (match) {
                        displays.push(Number.parseInt(match[1], 10));
                    }
                }
                return displays;
            }
        }
        throw new Error("failed to get displays");
    }
    process;
    _stdout;
    get stdout() {
        return this._stdout;
    }
    get exit() {
        return this.process.exit;
    }
    _screenWidth;
    get screenWidth() {
        return this._screenWidth;
    }
    _screenHeight;
    get screenHeight() {
        return this._screenHeight;
    }
    _videoStream;
    get videoStream() {
        return this._videoStream;
    }
    _controlMessageSerializer;
    get controlMessageSerializer() {
        return this._controlMessageSerializer;
    }
    _deviceMessageStream;
    get deviceMessageStream() {
        return this._deviceMessageStream;
    }
    constructor(options, process, stdout, videoStream, controlStream) {
        this.process = process;
        this._stdout = stdout;
        this._videoStream = videoStream
            .pipeThrough(options.createVideoStreamTransformer())
            .pipeThrough(new InspectStream((packet) => {
            if (packet.type === "configuration") {
                this._screenWidth = packet.data.croppedWidth;
                this._screenHeight = packet.data.croppedHeight;
            }
        }));
        if (controlStream) {
            this._controlMessageSerializer = new ScrcpyControlMessageSerializer(controlStream.writable, options);
            this._deviceMessageStream = controlStream.readable.pipeThrough(new ScrcpyDeviceMessageDeserializeStream());
        }
    }
    async close() {
        await this.process.kill();
    }
}
//# sourceMappingURL=client.js.map